import { useNavigate } from 'react-router';

import appStrings from '@/assets/constants/appStrings-no.ts';
import { routes } from '@/assets/constants/routes-no.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { SEO } from '@/lib/seo.tsx';
import { useAuth } from '@/services/Auth/index.ts';
import { Button } from '@/ui/button-2.tsx';

import Circle from '../../components/Circle/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import Disclaimer from '../Common/Disclaimer.tsx';

import HeaderSection from './HeaderSection.tsx';
import { RRS_CREDIT_CHECK_MYSELF } from './RRS.ts';

const linkTriggerClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline inline bg-transparent border-0 p-0 cursor-pointer text-left font-inherit';

const articleRowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const riskBarBg: Record<string, string> = {
  VeryHigh: 'bg-[#FA4969]',
  High: 'bg-[#FDC564]',
  Moderate: 'bg-[#18a0fb]',
  Low: 'bg-[#56BA82]',
  VeryLow: 'bg-[#0F853F]',
};

function riskBarHeightClass(id: string): string {
  if (id === 'High') return 'h-[35px]';
  if (id === 'VeryLow') return 'h-[105px]';
  return 'h-[70px]';
}

const riskGraph = [
  {
    id: 'VeryHigh',
    label: 'Svært høy risiko',
    value: '1-20',
    position: 'left' as const,
  },
  {
    id: 'High',
    label: 'Høy risiko',
    value: '21-29',
    position: 'right' as const,
  },
  {
    id: 'Moderate',
    label: 'Moderat risiko',
    value: '30-50',
    position: 'left' as const,
  },
  {
    id: 'Low',
    label: 'Lav risiko',
    value: '51-70',
    position: 'right' as const,
  },
  {
    id: 'VeryLow',
    label: 'Svært lav risiko',
    value: '71-100',
    position: 'left' as const,
  },
];

const CreditCheckMyself: React.FC = () => {
  const { login, isAnonymous } = useAuth();
  const navigate = useNavigate();

  const loginAction = async () => {
    sendUserPortalEvent('main_cta_clicked');
    if (isAnonymous) {
      await login('SCRIVE', routes.MY_SCOOPR);
    } else {
      await navigate(routes.MY_SCOOPR);
    }
  };

  return (
    <>
      <SEO
        title="Kredittsjekk deg selv gratis med BankID"
        description="Se kredittscoren din gratis, uten at det påvirker den. Logg inn med BankID og få full oversikt."
        path={routes.CREDIT_CHECK_MYSELF}
      />
      <RrsHandler {...RRS_CREDIT_CHECK_MYSELF} />
      <Section background="primary">
        <div className="hidden lg:block">
          <Circle
            className="pointer-events-none top-[-180px]! left-[-130px]!"
            size={380}
            border={80}
            color="lightTransparent"
          />
          <Circle
            className="pointer-events-none top-[160px]! right-[-190px]!"
            size={380}
            border={80}
            color="lightTransparent"
          />
        </div>

        <HeaderSection action={loginAction} />

        <div className="relative z-2 mx-auto w-full max-w-[900px]! text-white">
          <h1 className="mb-6 typography-v1-legacy-h2 sm:mb-8">
            {appStrings.MENU.CREDIT_CHECK_MYSELF}
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Har du noen gang lurt på hva som skjer når bankene utfører en kredittsjekk, eller
            hvordan man kan kredittsjekke seg selv? Her får du svarene.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Når du søker nytt lån går det noen dager før du får et brev med beskjed om at det er
            utført en kredittsjekk på deg, i det som kalles et gjenpartsbrev. Bankene er faktisk
            lovpålagt å kredittsjekke alle som søker lån. Enkelt forklart betyr dette at de sjekker
            betalingsevnen din – altså hva du har i gjeld, inntekt og formue, samt om du har noen
            betalingsanmerkninger.
            <br />
            <br />
            Summen av dette gir deg en kredittscore, som enten tilsier at det er en lav eller høy
            risiko for å låne deg penger. Jo høyere risiko, desto høyere renter får du på lånet
            ditt.
          </p>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={articleRowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan kredittsjekke meg selv?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Det å kredittsjekke seg selv er superenkelt, og du kan gjøre det mens du søker lån: Du
              fyller bare inn{' '}
              <button
                type="button"
                className={linkTriggerClassName}
                onClick={() => {
                  void loginAction();
                }}
              >
                opplysningene dine her
              </button>
              , logger inn med BankID – og venter. Etter kort tid får du både opp informasjonen du
              trenger for å kredittsjekke deg selv, og samtidig se hvor du får det beste
              lånetilbudet – som igjen er basert på kredittsjekken. Da får du både et innblikk i hva
              bankene kan se om deg, og du kan i fred og ro avgjøre hvilket lånetilbud du ønsker å
              akseptere.
            </p>

            <div className="mb-12 flex w-full flex-col sm:mb-14">
              {riskGraph.map(({ id, label, position, value }) => (
                <div className="flex w-full justify-between" key={id}>
                  <span className="flex w-[45%] flex-col items-end justify-center">
                    {position === 'left' ? (
                      <>
                        <p className="m-0 text-right typography-v1-legacy-p3ExtraBold">{label}</p>
                        <p className="m-0 text-right typography-v1-legacy-p3">{value}</p>
                      </>
                    ) : null}
                  </span>
                  <span
                    className={`mx-6 my-0.5 w-2 shrink-0 rounded-lg ${riskBarHeightClass(id)} ${riskBarBg[id] ?? ''}`}
                  />
                  <span className="flex w-[45%] flex-col justify-center">
                    {position === 'right' ? (
                      <>
                        <p className="m-0 text-left typography-v1-legacy-p3ExtraBold">{label}</p>
                        <p className="m-0 text-left typography-v1-legacy-p3">{value}</p>
                      </>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Kredittsjekk deg selv mens du søker lån
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Hvis kredittsjekken viser at du har en betalingsanmerkning, øker renten på lånet du
              søker. Skal du derfor få det aller beste tilbudet på lån, bør du kvitte deg med
              anmerkningene så fort som mulig. Det gjør du ganske enkelt ved å betale det du
              skylder.
              <br />
              <br />
              Uansett hva som er det økonomiske utgangspunktet ditt kan du sjekke hva som er det
              beste lånet for akkurat deg, via Scoopr. Når du søker lån hos Scoopr får du
              kredittsjekket deg selv, samtidig som du får tilbud fra ulike banker. La bankene
              konkurrere om å gi deg lavest mulig rente, så du slipper å betale mer enn du må.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan kredittsjekke en privatperson?{' '}
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Som nevnt vil en kredittsjekk si at en utlåner får tilgang til kredittopplysninger om
              en privatperson, og det skal alltid være en saklig grunn til å foreta en slik sjekk.
              <br />
              <br />
              Det kan være flere grunner til at du blir kredittsjekket enn bare at du søker lån, som
              for eksempel når du kjøper en mobil med abonnement. I noen tilfeller kan en også
              kredittsjekke personer som er aktuelle til en stilling med stort økonomisk ansvar.
              Mange utleiere lurer på om de kan kredittsjekke en leietaker, men det er i
              utgangspunktet ikke lov – med mindre leieforholdet foregår via en utleiemegler.
              <br />
              <br />
              Med andre ord kan ikke du som privatperson kredittsjekke noe andre enn deg selv.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <h3 className="mx-auto mt-14 mb-8 max-w-[900px]! text-center typography-v1-legacy-subtitle1Bold sm:mt-[136px] sm:mb-8">
            Scoopr hjelper deg med det også!
          </h3>
          <Button action={loginAction} color="yellow" className="mx-auto!">
            Vis min kredittscore
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </>
  );
};

export default CreditCheckMyself;
